/**
 * Detect when Taī wants live Notion meeting notes pulled before BLU answers,
 * and guess portal client id from free text.
 */

export function wantsMeetingSummaryInPortal(text) {
  return /create\s+a\s+meeting\s+summary\s+in\s+portal/i.test(String(text || ""));
}

export function wantsLiveNotionFetch(text) {
  const t = String(text || "").toLowerCase();
  if (wantsMeetingSummaryInPortal(text)) return true;
  if (!/\bnotion\b/.test(t)) return false;
  if (/\b(don't|do not|no need|skip|without)\s+(to\s+)?(fetch|pull|grab|load)\b/.test(t)) return false;
  const intent = /\b(pull|fetch|grab|retrieve|get|load|bring|look\s*up|lookup)\b/.test(t);
  const phrase = /(notion\s+history|from\s+notion|in\s+notion|meeting\s+notes)/.test(t);
  return intent || phrase;
}

/** True when BLU should pull portal Overview notes (and optionally Notion) for client context. */
export function wantsBluScopingContext(text) {
  if (wantsLiveNotionFetch(text)) return true;
  const t = String(text || "").toLowerCase();
  if (wantsMeetingSummaryInPortal(text)) return false;
  if (/\b(scope|scoping|scope of support)\b/.test(t)) return true;
  if (/\b(invoice|overview)\b/.test(t) && /\b(for|build|draft|write|create|generate|scope)\b/.test(t)) {
    return true;
  }
  return /\b(build|draft|write|create|generate)\b.*\b(scope|invoice)\b/.test(t);
}

/** Extract a portal note title when Taī names one explicitly in chat. */
export function extractPortalNoteTitleHint(text) {
  const source = String(text || "");
  const patterns = [
    /['']([^''\n]{3,140})['']/,
    /["]([^"\n]{3,140})["]/,
    /\b(?:named|titled|called)\s+['']([^''\n]+?)['']/i,
    /\b(?:named|titled|called)\s+"([^"\n]+?)"/i,
    /\b(?:in\s+(?:that\s+)?portal\s+)?named\s+['']?([^'""\n]+?)[''"]?(?:\s*--|\s*,|\s*\.|$)/i,
  ];
  for (const re of patterns) {
    const m = source.match(re);
    if (!m?.[1]) continue;
    const hint = m[1].trim().replace(/\s*--+\s*$/, "").trim();
    if (hint.length >= 3) return hint;
  }
  return null;
}

/**
 * Best-effort match: longest client name substring wins.
 */
export function guessClientIdFromMessage(text, clients) {
  if (!text || !Array.isArray(clients)) return null;
  const t = text.toLowerCase();
  let best = null;
  for (const c of clients) {
    const id = c?.id;
    const name = (c?.name || "").trim();
    if (!id || name.length < 2) continue;
    const nl = name.toLowerCase();
    if (t.includes(nl)) {
      if (!best || nl.length > best.len) best = { id, len: nl.length };
      continue;
    }
    const parts = name.split(/\s+/).filter((p) => p.length >= 3);
    for (const p of parts) {
      const pl = p.toLowerCase();
      if (t.includes(pl) && (!best || pl.length > best.len)) best = { id, len: pl.length };
    }
  }
  return best?.id ?? null;
}
